import {
  ConfigurableRoleOption,
  DEFAULT_ENGINEER_ROLES,
} from "resource-planner-model";
import { useSelector } from "react-redux";
import { AppState } from "../redux/AppTypes";

export interface EngineerRoleOption {
  id: string;
  label: string;
  grantsEngineerLeadAccess?: boolean;
}

interface EngineerRoleSource {
  role?: string;
}

const applicationConfigSelector = (state: AppState) =>
  state.applicationConfig?.item || {
    engineerRoles: DEFAULT_ENGINEER_ROLES,
  };

const engineersSelector = (state: AppState): EngineerRoleSource[] =>
  Object.values(state.engineers?.items || {}) as EngineerRoleSource[];

export function buildEngineerRoleOptions(
  engineerRoles: ConfigurableRoleOption[] = DEFAULT_ENGINEER_ROLES,
  engineers: EngineerRoleSource[] = [],
  extraRoleIds: string[] = []
): EngineerRoleOption[] {
  const optionsById = new Map<string, EngineerRoleOption>();
  const configuredById = new Map<string, ConfigurableRoleOption>();

  (engineerRoles || []).forEach((role) => {
    if (role.id) {
      configuredById.set(role.id, role);
    }
  });

  function addOption(id: string, role?: ConfigurableRoleOption): void {
    if (!id || optionsById.has(id)) {
      return;
    }
    optionsById.set(id, {
      id,
      label: role?.label || id,
      grantsEngineerLeadAccess: role?.grantsEngineerLeadAccess,
    });
  }

  (engineerRoles || [])
    .filter((role) => role.id && !role.archived)
    .sort((left, right) => left.order - right.order)
    .forEach((role) => addOption(role.id, role));

  engineers.forEach((engineer) => {
    const configured = engineer.role ? configuredById.get(engineer.role) : undefined;
    addOption(engineer.role || "", configured);
  });
  extraRoleIds.forEach((roleId) => {
    const configured = configuredById.get(roleId);
    addOption(roleId, configured);
  });

  return Array.from(optionsById.values());
}

export function getEngineerRoleLabel(
  options: EngineerRoleOption[],
  roleId?: string
): string {
  if (!roleId) {
    return "no role assigned";
  }
  return options.find((option) => option.id === roleId)?.label || roleId;
}

export function useEngineerRoles(extraRoleIds: string[] = []): EngineerRoleOption[] {
  const config = useSelector(applicationConfigSelector);
  const engineers = useSelector(engineersSelector);
  return buildEngineerRoleOptions(config.engineerRoles, engineers, extraRoleIds);
}
